import React, { useState } from "react";
import { StaticImage, getImage, withArtDirection } from "gatsby-plugin-image";
import { Box, Heading, Text } from "theme-ui";
import { graphql, navigate } from "gatsby";
import { SEO } from "../components";
import seoDetails from "@greythr/shared-components/data/seo_details.json";
import resellerData from "../../../main/src/data/partners/reseller.yml";
import { PartnerLogos, PlanTable } from "../components/partners/Reseller";
import { Banner, TakeAway } from "../components/partners";
import PartnerLogosID from "../components/partners/Reseller/PartnerLogosID";
import { getCommonData } from "@greythr/shared-utils/regionContent";
import {
  Button,
  colors,
  typography,
  ActionBanner,
} from "@greythr/shared-components";
import Layout from "../components/shared-components-wrapper/LayoutWrapper";

const ResellerPartner = ({
  data,
  pageContext: { region, partnerTypeCode, country },
}) => {
  const { bannerLg, bannerSm } = data;
  const banners = withArtDirection(getImage(bannerLg), [
    {
      media: "(max-width: 899px)",
      image: getImage(bannerSm),
    },
  ]);
  const displayPrimaryCTAMap = {
    IN: "/demo/?gc_source=reseller-partners&ac_form_tag=AF_DEMO_RESELLER_PARTNERS",
    ID: "/indonesia/demo/?gc_source=reseller-partners-id&ac_form_tag=AF_DEMO_RESELLER_PARTNERS_ID",
    "ID-id":
      "/indonesia/id/demo/?gc_source=reseller-partners-id&ac_form_tag=AF_DEMO_RESELLER_PARTNERS_ID-id",
  };
  const textData = getCommonData(region)?.resellerPartners;
  return (
    <Layout displayPrimaryCTA={displayPrimaryCTAMap[region]}>
      {banners && <Banner banners={banners} />}
      <Box sx={styles.wrapper}>
        <Heading as="h1" sx={styles.heading}>
          {textData?.heading}
        </Heading>
        <Text sx={styles.text} as="p">
          {textData?.subHeading}
        </Text>
        <Button
          variant="filled"
          size="large"
          onClick={() => navigate("#form-block")}
        >
          {textData?.CTA}
        </Button>
      </Box>
      <Box sx={styles.infographic}>
        {region === "IN" ? (
          <StaticImage
            src="../../static/images/partners/reseller-infographic.png"
            alt="Reseller India"
          />
        ) : region === "ID-id" ? (
          <StaticImage
            src="../../static/images/partners/reseller-bahasa-infographic.png"
            alt="Reseller Indonesia Bahasa"
          />
        ) : (
          <StaticImage
            src="../../static/images/partners/reseller-infographic-id.png"
            alt="Reseller Indonesia"
          />
        )}
      </Box>
      <TakeAway
        {...(region === "ID-id"
          ? resellerData?.takeAwayBahasa
          : resellerData?.takeAway)}
      />
      <PlanTable
        plans={
          region === "ID-id" ? resellerData.plansBahasa : resellerData.plans
        }
        region={region}
      />
      {region === "IN" ? (
        <PartnerLogos {...data} />
      ) : (
        <PartnerLogosID {...data} region={region} />
      )}
      <Box
        sx={{
          width: "80%",
          margin: "auto",
        }}
        id="form-block"
      >
        <iframe
          src="https://campaign.greythrconnect.com/l/1091962/2025-03-04/3n6w6d"
          width="100%"
          height="550"
          type="text/html"
          frameborder="0"
          allowTransparency="true"
          style={{
            border: `1px solid ${colors.neutral500}`,
            borderRadius: "1rem",
          }}
        ></iframe>
      </Box>
      <Box px={[3, 3, 4, 5]} sx={{ mt: ["4rem", "4rem", "7rem", "7rem"] }}>
        <ActionBanner
          variant="BodyBannerV3"
          theme="primary"
          title={textData?.actionBannerTitle}
        >
          <Button size="large" onClick={() => navigate("#form-block")}>
            {textData?.actionBannerCTAName}
          </Button>
        </ActionBanner>
      </Box>
    </Layout>
  );
};

export const partnerData = graphql`
  query ($partnerTypeCode: [String!], $country: String!) {
    bannerLg: file(relativePath: { eq: "partners/reseller-banner-lg.jpg" }) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, height: 526, layout: CONSTRAINED)
      }
    }
    bannerSm: file(relativePath: { eq: "partners/reseller-banner-sm.jpg" }) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, height: 863, layout: CONSTRAINED)
      }
    }
    strategicPartners: allDatoCmsPartner(
      filter: {
        partnerType: { eq: "Strategic Alliance Partner" }
        region: { regex: $country }
        enabled: { eq: true }
        showInPages: { elemMatch: { code: { in: $partnerTypeCode } } }
      }
      sort: { displayOrder: ASC }
    ) {
      nodes {
        partnerImageAltText
        partnerImage {
          gatsbyImageData(
            layout: CONSTRAINED
            placeholder: DOMINANT_COLOR
            height: 72
          )
        }
      }
    }
    awesomePartners: allDatoCmsPartner(
      filter: {
        partnerType: { eq: "Reseller Partners" }
        region: { regex: $country }
        enabled: { eq: true }
        showInPages: { elemMatch: { code: { in: $partnerTypeCode } } }
      }
      sort: { displayOrder: ASC }
    ) {
      nodes {
        partnerImageAltText
        partnerImage {
          gatsbyImageData(
            layout: CONSTRAINED
            placeholder: DOMINANT_COLOR
            height: 72
          )
        }
      }
    }
  }
`;

const styles = {
  wrapper: {
    px: [3, 3, 4, 5],
    maxWidth: "48.125rem",
    mx: "auto",
    textAlign: "center",
  },
  heading: {
    ...typography.h1,
    mb: "1.5rem",
  },
  text: {
    ...typography.textLg400,
    mb: "1.5rem",
  },
  infographic: {
    width: ["100%", "100%", "100%", "65%"],
    mx: "auto",
    py: [5, 5, "7rem"],
    px: [3, 3, 4],
  },
};

export default ResellerPartner;

export const Head = ({ location: { pathname }, pageContext: { region } }) => {
  const seo =
    seoDetails.resellerPartner[region.toLowerCase()] ||
    seoDetails.resellerPartner.in;
  return (
    <SEO
      title={seo.title}
      desc={seo.desc}
      pathname={pathname}
      hrefLang={true}
      availableInRegions="IN, ME, ID"
    />
  );
};
